import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Instagram, Menu, X, ChevronDown } from 'lucide-react';
import { useState, useEffect } from 'react';
import CookieBanner from './CookieBanner';
import NewsletterPopup from './NewsletterPopup';

const TikTokIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const Layout = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [serviziOpen, setServiziOpen] = useState(false);
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isAdminRoute) {
    return <Outlet />;
  }

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/viaggi', label: 'Viaggi' },
    { path: '/about', label: 'Chi siamo' },
    { path: '/contatti', label: 'Contatti' }
  ];

  const serviziLinks = [
    { path: '/collaborazioni', label: 'Collaborazioni' },
    { path: '/consulenza', label: 'Consulenze' }
  ];

  return (
    <div className="min-h-screen">
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'backdrop-blur-md bg-white/95' 
          : 'bg-primary'
      }`}>
        <div className="px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20 relative">
            <Link to="/" className="block lg:relative absolute left-1/2 -translate-x-1/2 lg:left-auto lg:translate-x-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_e3e4fbe6-20cb-4710-86c7-fffef37287f7/artifacts/qnvbghhc_logo%20chichi%20viaggio%20internet.png" 
                alt="Chichi in Viaggio" 
                className={`h-9 lg:h-12 w-auto transition-all duration-300 ${
                  scrolled ? 'brightness-100' : 'brightness-0 invert'
                }`}
              />
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-base font-medium transition-colors duration-300 ${
                    scrolled 
                      ? 'text-primary/80 hover:text-primary' 
                      : 'text-white/90 hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              
              <div 
                className="relative"
                onMouseEnter={() => setServiziOpen(true)}
                onMouseLeave={() => setServiziOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 text-base font-medium transition-colors duration-300 ${
                    scrolled 
                      ? 'text-primary/80 hover:text-primary' 
                      : 'text-white/90 hover:text-white'
                  }`}
                  data-testid="nav-servizi-dropdown"
                >
                  Servizi
                  <ChevronDown size={16} className={`transition-transform duration-300 ${serviziOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {serviziOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 py-2 bg-white rounded-lg shadow-lg min-w-[180px]"
                    >
                      {serviziLinks.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          className="block px-4 py-2 text-base font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                          data-testid={`dropdown-${link.label.toLowerCase()}`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <a
                href="https://instagram.com/chichi_inviaggio"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 text-base font-medium transition-colors duration-300 ${
                  scrolled 
                    ? 'text-primary/80 hover:text-primary' 
                    : 'text-white/90 hover:text-white'
                }`}
                data-testid="nav-instagram-link"
              >
                <Instagram size={18} />
                Seguici
              </a>
            </div>

            <button
              className={`lg:hidden transition-colors duration-300 ${
                scrolled ? 'text-primary' : 'text-white'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`lg:hidden relative z-50 ${
                scrolled ? 'bg-white border-t border-primary/20' : 'bg-primary border-t border-white/20'
              }`}
              data-testid="mobile-menu"
            >
              <div className="px-6 py-6 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block text-base font-medium transition-colors py-2 ${
                      scrolled 
                        ? 'text-primary hover:text-primary/80' 
                        : 'text-white hover:text-white/80'
                    }`}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
                
                <div className="space-y-2">
                  <button
                    onClick={() => setServiziOpen(!serviziOpen)}
                    className={`flex items-center justify-between w-full text-base font-medium transition-colors py-2 ${
                      scrolled 
                        ? 'text-primary hover:text-primary/80' 
                        : 'text-white hover:text-white/80'
                    }`}
                  >
                    Servizi
                    <ChevronDown size={16} className={`transition-transform duration-300 ${serviziOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {serviziOpen && (
                    <div className="pl-4 space-y-2">
                      {serviziLinks.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`block text-sm font-medium transition-colors py-2 ${
                            scrolled 
                              ? 'text-primary/70 hover:text-primary' 
                              : 'text-white/70 hover:text-white'
                          }`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="border-t pt-4 mt-4 space-y-3" style={{ borderColor: scrolled ? 'rgba(196, 165, 114, 0.2)' : 'rgba(255, 255, 255, 0.2)' }}>
                  <a
                    href="https://instagram.com/chichi_inviaggio"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 text-base font-medium transition-colors py-2 ${
                      scrolled 
                        ? 'text-primary hover:text-primary/80' 
                        : 'text-white hover:text-white/80'
                    }`}
                    data-testid="mobile-instagram-link"
                  >
                    <Instagram size={20} />
                    Instagram
                  </a>
                  <a
                    href="https://www.tiktok.com/@chichi_inviaggio"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 text-base font-medium transition-colors py-2 ${
                      scrolled 
                        ? 'text-primary hover:text-primary/80' 
                        : 'text-white hover:text-white/80'
                    }`}
                    data-testid="mobile-tiktok-link"
                  >
                    <TikTokIcon />
                    TikTok
                  </a>
                  <a
                    href="https://www.facebook.com/share/1LTLixUiXc/?mibextid=wwXIfr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 text-base font-medium transition-colors py-2 ${
                      scrolled 
                        ? 'text-primary hover:text-primary/80' 
                        : 'text-white hover:text-white/80'
                    }`}
                    data-testid="mobile-facebook-link"
                  >
                    <FacebookIcon />
                    Facebook
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main>
        <Outlet />
      </main>

      <CookieBanner />
      <NewsletterPopup />

      <footer className="bg-primary text-primary-foreground py-12 md:py-16">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 pb-8 border-b border-primary-foreground/20">
              <div className="flex flex-col items-center md:items-start">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e3e4fbe6-20cb-4710-86c7-fffef37287f7/artifacts/qnvbghhc_logo%20chichi%20viaggio%20internet.png" 
                  alt="Chichi in Viaggio" 
                  className="h-12 w-auto brightness-0 invert mb-3" 
                />
                <p className="text-primary-foreground/70 text-sm text-center md:text-left">
                  SLOW FAMILY TRAVEL
                </p>
              </div>

              <div className="flex items-center gap-6">
                <a
                  href="https://instagram.com/chichi_inviaggio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-all duration-300 hover:scale-110"
                  data-testid="footer-instagram-link"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href="https://www.tiktok.com/@chichi_inviaggio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-all duration-300 hover:scale-110"
                  data-testid="footer-tiktok-link"
                >
                  <TikTokIcon />
                </a>
                <a
                  href="https://www.facebook.com/share/1LTLixUiXc/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-all duration-300 hover:scale-110"
                  data-testid="footer-facebook-link"
                >
                  <FacebookIcon />
                </a>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            
            <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-center">
              <p className="text-primary-foreground/60 text-sm">
                © 2026 chichiinviaggio — P.IVA 04569820162. Tutti i diritti riservati.
              </p>
              <div className="flex items-center gap-4 text-xs text-primary-foreground/50">
                <Link to="/privacy-policy" className="hover:text-primary-foreground transition-colors" data-testid="footer-privacy-link">
                  Privacy Policy
                </Link>
                <span>·</span>
                <Link to="/termini-condizioni" className="hover:text-primary-foreground transition-colors" data-testid="footer-termini-link">
                  Termini e Condizioni
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;